import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_LOCALE,
  ORG_NAME,
  SITE_URL,
  SUPPORTED_LOCALES,
  TWITTER_HANDLE,
  type Locale,
  type PublicRoute,
} from './siteConfig';
import { getRouteMeta } from './routeMeta';
import {
  faqPageSchema,
  organizationSchema,
  softwareApplicationSchema,
  type FaqItem,
} from './jsonLd';

interface SeoProps {
  route: PublicRoute;
  faqs?: FaqItem[];
  includeSoftwareApplication?: boolean;
  noIndex?: boolean;
}

function isSupportedLocale(value: string | undefined): value is Locale {
  return value === 'es' || value === 'en';
}

// Single per-page SEO surface. Emits title/description/canonical, Open Graph
// and Twitter cards, hreflang alternates for both supported locales, and the
// JSON-LD schemas appropriate to the page (Organization always; optional
// SoftwareApplication and FAQPage).
//
// Hreflang URLs currently point at the same path because URL-based i18n
// prefixes (/es, /en) are not yet wired. Once routing is updated, only the
// alternateUrl computation below needs to change.
export const Seo: React.FC<SeoProps> = ({
  route,
  faqs,
  includeSoftwareApplication = false,
  noIndex = false,
}) => {
  const { i18n } = useTranslation();
  const localeCandidate = (i18n.language || DEFAULT_LOCALE).split('-')[0];
  const locale: Locale = isSupportedLocale(localeCandidate) ? localeCandidate : DEFAULT_LOCALE;
  const meta = getRouteMeta(locale, route);

  const canonical = `${SITE_URL}${route === '/' ? '' : route}`;
  const alternateUrl = (_l: Locale) => canonical; // pre-prefix-routing placeholder

  const jsonLdBlocks: Record<string, unknown>[] = [organizationSchema()];
  if (includeSoftwareApplication) {
    jsonLdBlocks.push(softwareApplicationSchema(locale));
  }
  if (faqs && faqs.length > 0) {
    jsonLdBlocks.push(faqPageSchema(faqs));
  }

  return (
    <Helmet>
      <html lang={locale} />
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      {meta.keywords && <meta name="keywords" content={meta.keywords} />}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      <link rel="canonical" href={canonical} />
      {SUPPORTED_LOCALES.map((l) => (
        <link key={l} rel="alternate" hrefLang={l} href={alternateUrl(l)} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={alternateUrl(DEFAULT_LOCALE)} />

      <meta property="og:type" content={meta.ogType ?? 'website'} />
      <meta property="og:site_name" content={ORG_NAME} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonical} />
      {meta.ogImage && <meta property="og:image" content={meta.ogImage} />}
      <meta property="og:locale" content={locale === 'es' ? 'es_LA' : 'en_US'} />
      {SUPPORTED_LOCALES.filter((l) => l !== locale).map((l) => (
        <meta key={l} property="og:locale:alternate" content={l === 'es' ? 'es_LA' : 'en_US'} />
      ))}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      {meta.ogImage && <meta name="twitter:image" content={meta.ogImage} />}

      {jsonLdBlocks.map((block, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};
